import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { ExternalAccountsAPI } from "@/features/external-accounts/api/external-accounts";
import type {
  ExternalAccount,
  RemoteAddressbook,
  RemoteCalendar,
} from "@/features/external-accounts/types";
import { useTranslation } from "@/features/utils/i18n";
import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";

interface CalendarWithAccount extends RemoteCalendar {
  account_id: string;
  account_name: string;
  account_provider: string;
}

interface AddressbookWithAccount extends RemoteAddressbook {
  account_id: string;
  account_name: string;
  account_provider: string;
}

interface SyncSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  // Optional: specify a newly created account to focus on
  newAccountId?: string;
}

const CREATE_HEYUP = "__CREATE_HEYUP__";
const NONE = "__NONE__";

export function SyncSettingsDialog({
  open,
  onOpenChange,
  clientId,
  newAccountId,
}: SyncSettingsDialogProps) {
  const { t } = useTranslation("externalAccounts");
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<ExternalAccount[]>([]);
  const [calendars, setCalendars] = useState<CalendarWithAccount[]>([]);
  const [addressbooks, setAddressbooks] = useState<AddressbookWithAccount[]>(
    [],
  );
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(
    NONE,
  );
  const [selectedAddressbookId, setSelectedAddressbookId] = useState<
    string | null
  >(NONE);
  const [initialLoad, setInitialLoad] = useState(true);

  const loadAccounts = async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      const [allAccounts, allCalendars, allAddressbooks, currentSettings] =
        await Promise.all([
          ExternalAccountsAPI.getAccounts(clientId),
          ExternalAccountsAPI.listCalendars(clientId),
          ExternalAccountsAPI.listAddressbooks(clientId),
          ExternalAccountsAPI.getSettings(clientId),
        ]);

      setAccounts(allAccounts);
      setCalendars(allCalendars);
      setAddressbooks(allAddressbooks);

      // Load current settings if they exist
      if (currentSettings) {
        if (currentSettings.primary_calendar_id) {
          setSelectedCalendarId(currentSettings.primary_calendar_id);
        }
        if (currentSettings.contacts_addressbook_id) {
          setSelectedAddressbookId(currentSettings.contacts_addressbook_id);
        }
      }

      // If a new account was just created, pre-select CREATE_HEYUP on that account
      // This overrides current settings
      if (newAccountId) {
        const newAccount = allAccounts.find((a) => a.id === newAccountId);
        if (newAccount) {
          if (newAccount.capabilities.capabilities.includes("calendar")) {
            // Check if heyup.ai calendar exists
            const heyupCal = allCalendars.find(
              (cal) => cal.name.toLowerCase() === "heyup.ai",
            );
            if (heyupCal) {
              setSelectedCalendarId(heyupCal.id);
            } else if (newAccount.supports_calendar_create) {
              // Only auto-select CREATE_HEYUP if the account supports creation
              setSelectedCalendarId(`${CREATE_HEYUP}::${newAccount.id}`);
            }
          }
          if (newAccount.capabilities.capabilities.includes("contacts")) {
            // Check if heyup.ai addressbook exists anywhere
            const heyupAb = allAddressbooks.find(
              (ab) => ab.name.toLowerCase() === "heyup.ai",
            );

            if (heyupAb) {
              // Select the existing heyup.ai addressbook
              setSelectedAddressbookId(heyupAb.id);
            } else if (newAccount.supports_contacts_book_create) {
              // Only auto-select CREATE_HEYUP if the account supports creation
              setSelectedAddressbookId(`${CREATE_HEYUP}::${newAccount.id}`);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error loading accounts:", error);
      toast.error(t("externalAccounts.messages.loadAccountsError"));
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadAccounts();
    } else {
      // Clear all state when dialog closes
      setTimeout(() => {
        setAccounts([]);
        setCalendars([]);
        setAddressbooks([]);
        setSelectedCalendarId(NONE);
        setSelectedAddressbookId(NONE);
        setInitialLoad(true);
      }, 200); // Small delay to avoid visual glitch during close animation
    }
  }, [open, clientId, newAccountId]);

  const handleSave = async () => {
    if (!clientId) return;

    try {
      setLoading(true);

      const settingsUpdate: {
        primary_calendar_create?: {
          account_id: string;
          name: string;
        };
        primary_calendar_id?: string;
        primary_calendar_name?: string;
        primary_calendar_account_id?: string;
        contacts_addressbook_create?: {
          account_id: string;
          name: string;
        };
        contacts_addressbook_id?: string;
        contacts_addressbook_name?: string;
        contacts_addressbook_account_id?: string;
      } = {};

      // Handle calendar selection
      if (selectedCalendarId) {
        if (selectedCalendarId === NONE) {
          // Clear calendar sync
          settingsUpdate.primary_calendar_id = "";
          settingsUpdate.primary_calendar_name = "";
          settingsUpdate.primary_calendar_account_id = "";
        } else if (selectedCalendarId.startsWith(CREATE_HEYUP)) {
          // Request calendar creation
          const accountId = selectedCalendarId.split("::")[1];
          if (!accountId) {
            toast.error(t("externalAccounts.messages.invalidSelection"));
            setLoading(false);
            return;
          }

          settingsUpdate.primary_calendar_create = {
            account_id: accountId,
            name: "heyup.ai",
          };
          toast.info(t("externalAccounts.messages.creatingCalendar"));
        } else {
          // Use existing calendar
          const calendar = calendars.find((c) => c.id === selectedCalendarId);
          if (calendar) {
            settingsUpdate.primary_calendar_id = calendar.id;
            settingsUpdate.primary_calendar_name = calendar.name;
            settingsUpdate.primary_calendar_account_id = calendar.account_id;
          }
        }
      }

      // Handle addressbook selection
      if (selectedAddressbookId) {
        if (selectedAddressbookId === NONE) {
          // Clear addressbook sync
          settingsUpdate.contacts_addressbook_id = "";
          settingsUpdate.contacts_addressbook_name = "";
          settingsUpdate.contacts_addressbook_account_id = "";
        } else if (selectedAddressbookId.startsWith(CREATE_HEYUP)) {
          // Request addressbook creation
          const accountId = selectedAddressbookId.split("::")[1];
          if (!accountId) {
            toast.error(t("externalAccounts.messages.invalidSelection"));
            setLoading(false);
            return;
          }

          settingsUpdate.contacts_addressbook_create = {
            account_id: accountId,
            name: "heyup.ai",
          };
          toast.info(t("externalAccounts.messages.creatingAddressbook"));
        } else {
          // Use existing addressbook
          const addressbook = addressbooks.find(
            (ab) => ab.id === selectedAddressbookId,
          );
          if (addressbook) {
            settingsUpdate.contacts_addressbook_id = addressbook.id;
            settingsUpdate.contacts_addressbook_name = addressbook.name;
            settingsUpdate.contacts_addressbook_account_id =
              addressbook.account_id;
          }
        }
      }

      // Save all settings in one call
      await ExternalAccountsAPI.updateSettings(clientId, settingsUpdate);

      toast.success(t("externalAccounts.messages.syncConfigured"));
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(t("externalAccounts.messages.syncError"));
    } finally {
      setLoading(false);
    }
  };

  // Filter accounts by capabilities
  const calendarAccounts = accounts.filter((acc) =>
    acc.capabilities.capabilities.includes("calendar"),
  );
  const contactsAccounts = accounts.filter((acc) =>
    acc.capabilities.capabilities.includes("contacts"),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("externalAccounts.syncDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("externalAccounts.syncDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {initialLoad && loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Spinner className="mb-3" />
              <p className="text-sm">
                {t("externalAccounts.syncDialog.loading")}
              </p>
            </div>
          ) : (
            <>
              {/* Calendar Selection */}
              {calendarAccounts.length > 0 && (
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium mb-1">
                      {t("externalAccounts.syncDialog.calendar")}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {t("externalAccounts.syncDialog.calendarDescription")}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Select
                      value={selectedCalendarId || ""}
                      onValueChange={(value) => {
                        setSelectedCalendarId(value || null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "externalAccounts.syncDialog.selectCalendar",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>
                          {t("externalAccounts.syncDialog.dontSyncCalendars")}
                        </SelectItem>
                        {calendarAccounts
                          .filter((account) => account.supports_calendar_create)
                          .map((account, i) => {
                            const existingCalendar = calendars.find(
                              (a) =>
                                a.account_id === account.id &&
                                a.name === "heyup.ai",
                            );
                            return (
                              <Fragment key={account.id}>
                                {i === 0 && <SelectSeparator />}
                                <SelectItem
                                  value={
                                    existingCalendar
                                      ? existingCalendar.id
                                      : `${CREATE_HEYUP}::${account.id}`
                                  }
                                >
                                  {existingCalendar
                                    ? t("externalAccounts.syncDialog.use")
                                    : t(
                                        "externalAccounts.syncDialog.create",
                                      )}{" "}
                                  {t("externalAccounts.syncDialog.calendarOn", {
                                    accountName: account.name,
                                    provider: account.provider,
                                  })}
                                </SelectItem>
                              </Fragment>
                            );
                          })}
                        <SelectSeparator />
                        {calendars
                          ?.filter((cal) => {
                            if (cal.name.toLowerCase() === "heyup.ai")
                              return false;
                            if (cal.read_only) return false;
                            // Filter out calendars from accounts that don't support writing
                            const account = accounts.find(
                              (a) => a.id === cal.account_id,
                            );
                            return account?.supports_calendar_write !== false;
                          })
                          .map((calendar) => (
                            <SelectItem key={calendar.id} value={calendar.id}>
                              {calendar.name} - {calendar.account_name} (
                              {calendar.account_provider})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Addressbook Selection */}
              {contactsAccounts.length > 0 && (
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium mb-1">
                      {t("externalAccounts.syncDialog.addressbook")}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {t("externalAccounts.syncDialog.addressbookDescription")}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Select
                      value={selectedAddressbookId || ""}
                      onValueChange={(value) => {
                        setSelectedAddressbookId(value || null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "externalAccounts.syncDialog.selectAddressbook",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>
                          {t("externalAccounts.syncDialog.dontSyncContacts")}
                        </SelectItem>

                        {contactsAccounts.map((account, i) => {
                          const existingAb = addressbooks.find(
                            (ab) =>
                              ab.account_id === account.id &&
                              ab.name.toLowerCase() === "heyup.ai",
                          );

                          return (
                            <Fragment key={account.id}>
                              {i === 0 && <SelectSeparator />}
                              <SelectItem
                                value={
                                  existingAb
                                    ? existingAb.id
                                    : `${CREATE_HEYUP}::${account.id}`
                                }
                              >
                                {existingAb
                                  ? t("externalAccounts.syncDialog.use")
                                  : t(
                                      "externalAccounts.syncDialog.create",
                                    )}{" "}
                                {t(
                                  "externalAccounts.syncDialog.addressbookOn",
                                  {
                                    accountName: account.name,
                                    provider: account.provider,
                                  },
                                )}
                              </SelectItem>
                            </Fragment>
                          );
                        })}
                        <SelectSeparator />
                        {addressbooks
                          .filter((ab) => {
                            if (ab.name.toLowerCase() === "heyup.ai")
                              return false;
                            if (ab.read_only) return false;
                            // Filter out addressbooks from accounts that don't support writing
                            const account = accounts.find(
                              (a) => a.id === ab.account_id,
                            );
                            return account?.supports_contacts_write !== false;
                          })
                          .map((addressbook) => (
                            <SelectItem
                              key={addressbook.id}
                              value={addressbook.id}
                            >
                              {addressbook.name} - {addressbook.account_name} (
                              {addressbook.account_provider})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {newAccountId
              ? t("externalAccounts.buttons.skipForNow")
              : t("externalAccounts.buttons.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Spinner variant="circle" />}
            {t("externalAccounts.buttons.saveSettings")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
